import { type QueryFunctionContext, useQuery } from "@tanstack/react-query";
import { CHART_PERIOD } from "hooks/useChartPeriodMeasure";
import { type DataPoint } from "components/charts/types/HorizontalBarChart";
import { copilotReportingService } from "features/Reporting/services/copilotReportingService";
import { COPILOT_APP_DISPLAY, COPILOT_APP_COLOR } from "./constants";
import type { CopilotAppEngagementTrendPoint, FlattenedCopilotUsageRecord } from "./types";
import {
  largestToSmallest,
  lineChartFactory,
  type PerformanceWithAccountFilter,
} from "features/Library/Common/utils/performanceUtils";
import { type BarChartData } from "components/charts/types/Shared";
import { SERVER_DATE_ONLY_FORMAT } from "utils/dateTimeUtils";
import moment from "moment";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";

type QueryContext = QueryFunctionContext<[_key: string, filter: PerformanceWithAccountFilter]>;

export function flattenCopilotUsageRecord(record: CopilotAppEngagementTrendPoint) {
  const flat = { Date: record.Date } as FlattenedCopilotUsageRecord;

  const set = <T extends keyof FlattenedCopilotUsageRecord>(key: T, value: FlattenedCopilotUsageRecord[T]) => {
    flat[key] = value;
  };

  for (const u of record.CopilotUsage) {
    const suffix = COPILOT_APP_DISPLAY[u.CopilotApps];
    if (!suffix) continue;

    set(`ActiveUsers${suffix}` as keyof FlattenedCopilotUsageRecord, u.ActiveUsers);
    set(`EnabledUsers${suffix}` as keyof FlattenedCopilotUsageRecord, u.EnabledUsers);
    set(`Utilization${suffix}` as keyof FlattenedCopilotUsageRecord, u.Utilization);
  }

  return flat;
}

export const flattenCopilotUsage = (records: CopilotAppEngagementTrendPoint[]) =>
  records.map(flattenCopilotUsageRecord);

const getCopilotUsersTrend = async ({ signal, queryKey }: QueryContext) => {
  const [_key, filter] = queryKey;
  const { data } = await copilotReportingService.getUsersTrend(filter, signal);

  const formattedData = lineChartFactory(data);

  const lineData = {
    date: formattedData.Date,
    activeUsers: formattedData.ActiveUsers,
  };

  const barData: BarChartData<{ activeUsers: number }>[] = data.map((item) => {
    const activeUsers = item.ActiveUsers;
    return {
      group: moment(item.Date).format(SERVER_DATE_ONLY_FORMAT),
      activeUsers,
    };
  });

  const order: (keyof { activeUsers: number })[] = ["activeUsers"];

  return {
    lineData,
    barData,
    order,
  };
};

const getCopilotAppEngagementTrend = async ({ signal, queryKey }: QueryContext) => {
  const [_key, filter] = queryKey;
  const { data } = await copilotReportingService.getAppEngagement(filter, signal);

  const bars: DataPoint[] = data
    .map((item) => {
      const appKey = item.CopilotApp;
      const display = COPILOT_APP_DISPLAY[appKey];
      const fillColor = COPILOT_APP_COLOR[appKey];
      return {
        id: appKey,
        category: display,
        value: item.Utilization * 100,
        fillColor,
      };
    })
    .sort(largestToSmallest);

  const filteredBarData = bars.filter((bar) => bar.value > 0);

  return { bars: filteredBarData, rawData: data };
};

const getCopilotUserEngagement = async ({ signal, queryKey }: QueryContext) => {
  const [_key, filter] = queryKey;
  const { data } = await copilotReportingService.getUserEngagement(
    { dateFrom: filter.dateFrom, dateTo: filter.dateTo },
    signal,
  );
  return data;
};

const getCopilotAppEngagementTimeseries = async ({ signal, queryKey }: QueryContext) => {
  const [_key, filter] = queryKey;
  const { data } = await copilotReportingService.getAppEngagementTrend(filter, signal);

  const flatData = flattenCopilotUsage(data);

  const toPercentUtilization = <T extends Record<string, unknown>>(data: T[]) =>
    data.map((row) => {
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(row)) {
        out[key] =
          key.startsWith("Utilization") && typeof value === "number" ? parseInt(roundToTwoDigits(value * 100)) : value;
      }
      return out as T;
    });

  const percentData = toPercentUtilization(flatData);

  const formattedData = lineChartFactory(percentData);

  return formattedData;
};

export const useCopilotUsersTrend = (filter: PerformanceWithAccountFilter) => {
  return useQuery({
    queryKey: ["copilot-users-trend", filter],
    enabled: filter.aggregation !== CHART_PERIOD.UNSET,
    queryFn: getCopilotUsersTrend,
  });
};

export const useCopilotAppEngagementTrend = (filter: PerformanceWithAccountFilter) => {
  return useQuery({
    queryKey: ["copilot-app-engagement-trend", filter],
    enabled: filter.aggregation !== CHART_PERIOD.UNSET,
    queryFn: getCopilotAppEngagementTrend,
  });
};

export const useCopilotUserEngagement = (filter: PerformanceWithAccountFilter) => {
  return useQuery({
    queryKey: ["copilot-user-engagement", filter],
    queryFn: getCopilotUserEngagement,
  });
};

export const useCopilotAppEngagementTimeseries = (filter: PerformanceWithAccountFilter) => {
  return useQuery({
    queryKey: ["copilot-app-engagement-timeseries", filter],
    enabled: filter.aggregation !== CHART_PERIOD.UNSET,
    queryFn: getCopilotAppEngagementTimeseries,
  });
};
